import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import useRealtimeSync from "../../useRealtimeSync";
import authFetch, { clearAuthToken } from "../../authFetch";
import { isNative, checkBiometrics, verifyBiometric, isBiometricLockEnabled, setBiometricLockEnabled, hapticTap, hapticSuccess } from "../../native";
import T from "../../i18n/translations";
import PlatformCtx from "../../state/PlatformCtx";
import SEED from "../../state/seed";
import { TODAY, LABEL, setCurrency } from "../../utils/format";
import { fireNotification } from "../../notifications";
import Ico from "../../shared/icons";
import { LoadingSplash } from "../../shared/SplashScreen";
import OfflineBanner from "../../shared/OfflineBanner";
import NotifBell from "../../shared/NotifBell";
import ErrorBoundary from "../../shared/ErrorBoundary";
import useBrandingTheme from "../auth/useBrandingTheme";
import Login from "../auth/Login";
import BottomNav from "./BottomNav";
import CalendarModule from "../calendar/CalendarModule";
import Execution from "../execution/Execution";
import AIWorkspace from "../ai/AIWorkspace";
import FilesModule from "../files/FilesModule";
import TaskEnginePanel from "../tasks/TaskEnginePanel";
import ReportsModule from "../finance/reports/ReportsModule";
import IncomeModule from "../finance/income/IncomeModule";
import ExpensesModule from "../finance/expenses/ExpensesModule";
import Investments from "../finance/investments/Investments";
import Fleet from "../fleet/Fleet";
import HouseholdOps from "../household/HouseholdOps";
import FacilityMgmt from "../facility/FacilityMgmt";
import StaffModule from "../staff/StaffModule";
import MenuModule from "../menu/MenuModule";
import SuppliersModule from "../suppliers/SuppliersModule";
import TransportModule, { PlacesAutocomplete } from "../transport/TransportModule";
import ProjectsModule from "../projects/ProjectsModule";
import SmartHomeModule from "../smarthome/SmartHomeModule";
import CommandCenter from "../dashboard/CommandCenter";
import OnboardingWizard from "../onboarding/OnboardingWizard";
import HelpBubble from "../help/HelpBubble";
import StaffView from "../staff/StaffView";
import { isStaffUser } from "../../permissions/checks";
import WorkspaceSettings from "../../settings/WorkspaceSettings";
import Settings from "../../settings/Settings";
import MoyasarPaymentInit from "../../platform/MoyasarPaymentInit";
import { ALL_MODULE_KEYS as REGISTRY_MODULE_KEYS, MODULE_GROUPS as REGISTRY_MODULE_GROUPS, moduleLabel } from "../registry";

/* Build workspace modules object from a selected packageId */
export const ALL_MODULE_KEYS = REGISTRY_MODULE_KEYS;
export function buildModulesFromPackage(packageId,packages){
  const pkg=(packages||[]).find(p=>p.id===packageId);
  if(!pkg)return {}; /* no package selected → all modules default-on */
  const enabled=new Set(pkg.modules||[]);
  const mods={};
  /* Only write `true` for included modules. Omitting excluded ones (rather
     than setting them to `false`) means stored mods don't shadow future
     package updates — when super admin adds a new module to the package,
     pkgAllows() picks it up without needing each workspace to re-run
     buildModulesFromPackage. mods[k]===false stays reserved for explicit
     user toggles in Settings → Branding → Modules. */
  enabled.forEach(k=>{mods[k]=true;});
  mods.Dashboard=true; /* always on */
  return mods;
}

export default
function WorkspaceApp({wsKey="tros_v6", wsId="default", onImpersonationExit, onLogoutToMain, pdispatch, wsInfo, platform:platformProp, autoLoginUser=null}){
  /* Context always wins — it's updated synchronously when super admin changes labels */
  const platformCtx = useContext(PlatformCtx);
  const platform = (platformCtx && Object.keys(platformCtx).length) ? platformCtx : platformProp;
  const {state:syncState,dispatch,forceUpdate,loading:syncLoading,syncError}=useRealtimeSync(wsKey,SEED);
  const sessionKey=`${wsKey}_session`;
  const [currentUser,setCurrentUser]=useState(()=>{
    try{const r=localStorage.getItem(sessionKey);if(r)return JSON.parse(r);}catch{}
    // Platform login already authenticated this user — skip workspace login entirely
    if(autoLoginUser){try{localStorage.setItem(sessionKey,JSON.stringify(autoLoginUser));}catch{}}
    return autoLoginUser||null;
  });

  /* ── Subscription / trial access gating ── */
  const [subStatus,setSubStatus]=useState({access:true,status:"active"});
  const checkSub=useCallback(()=>{
    if(!wsKey)return;
    authFetch(`/api/check-subscription?ws_key=${encodeURIComponent(wsKey)}`)
      .then(r=>r.json())
      .then(d=>{if(d)setSubStatus(d);})
      .catch(()=>{/* fail open */});
  },[wsKey]);
  useEffect(()=>{checkSub();},[checkSub]);
  /* Re-check subscription once every 24 hours */
  useEffect(()=>{
    if(!wsKey)return;
    const iv=setInterval(checkSub,24*60*60*1000);
    return()=>clearInterval(iv);
  },[wsKey,checkSub]);
  /* Listen for 403 subscription_expired from data writes */
  useEffect(()=>{
    const handler=(e)=>{if(e.detail==="subscription_expired")checkSub();};
    window.addEventListener("subscription:expired",handler);
    return()=>window.removeEventListener("subscription:expired",handler);
  },[checkSub]);

  const [view,setView]=useState("Dashboard");
  const [sidebarOpen,setSidebarOpen]=useState(false);
  const [locDropOpen,setLocDropOpen]=useState(false);
  const [locSearch,setLocSearch]=useState("");
  const locDropRef=useRef(null);
  const [financeTab,setFinanceTab]=useState("income");
  const [opsTab,setOpsTab]=useState("household");
  const [lang,setLang]=useState(syncState.lang||"en");

  const safe={...SEED,...syncState,lang,wsKey,currentUser,
    /* Platform-level module subtitle overrides win over workspace-level ones */
    moduleSubtitles:{...(syncState.moduleSubtitles||{}), ...(platform?.platformModuleSubtitles||{})},
    /* Platform-level module name overrides — used in page titles */
    platformModuleNames: platform?.platformModuleNames||{},
    /* Task engine: platform-level configs propagate to all workspaces.
       Q11 plan: migrate to table-only. Blob merge stays as a read-only overlay
       until every workspace has been flushed via SATaskConfigsPanel → "Flush
       to workspace". Then drop this spread in a follow-up cleanup.
       Phase 6.2: platform.taskTypes removed — taxonomy (seed → platform →
       workspace) is now the single source for task type vocab. */
    taskConfigs: [...(syncState.taskConfigs||[]), ...(platform?.taskEngineConfigs||[]).filter(pc=>!(syncState.taskConfigs||[]).some(wc=>wc.id===pc.id))],
    /* Task visibility: workspace overrides win over platform defaults */
    taskVisibilityRules: {...(platform?.taskVisibilityRules||{}), ...(syncState.taskVisibilityOverrides||{})},
    /* Workspace colors enforced from platform defaults — single anchor drives all colours */
    branding: {
      ...(syncState.branding||SEED.branding||{}),
      anchorColor: platform?.wsDefaultAnchorColor||platform?.wsDefaultAccentColor||"#C9A84C",
      themeMode:   platform?.wsDefaultThemeMode||"light",
    },
  };
  setCurrency(safe.currency || platform?.platformCurrency || "SAR");
  useEffect(()=>{ window._dispatch = dispatch; }, [dispatch]);

  /* ── Auto-complete onboarding for existing users ──
     If an owner has meaningful workspace data but the flag
     hasn't been set, backfill complete=true silently so
     the coach-mark never surfaces. Bypassed when the user
     has explicitly hit "Restart Onboarding" in Settings,
     which sets manuallyRestarted=true. Also bypassed when
     the user is actively mid-wizard (startedAt is set but
     completedAt isn't) — otherwise the act of *doing* the
     wizard's first action (adding a staff member) would
     instantly dismiss the wizard via this backfill. Runs
     once per mount in any case. */
  const _autoCompletedRef=useRef(false);
  useEffect(()=>{
    if(_autoCompletedRef.current) return;
    if(syncLoading) return;
    const isOwner=currentUser?.role==="owner";
    const onb=safe.onboardingState||{};
    if(!isOwner || onb.complete===true) return;
    /* Honour explicit restart — user wants to replay even
       though their workspace clearly has data. */
    if(onb.manuallyRestarted===true) return;
    /* If the wizard is in progress (started but not finished),
       don't auto-backfill on data growth — the user is
       building that data via the wizard itself. */
    if(onb.startedAt && !onb.completedAt) return;
    const hasData=(
      (safe.staff||[]).length>0 ||
      (safe.vehicles||[]).length>0 ||
      (safe.expenses||[]).length>0 ||
      (safe.income||[]).length>0 ||
      (safe.maintenance||[]).length>0 ||
      (safe.groceries||[]).length>0 ||
      (safe.suppliers||[]).length>0 ||
      (safe.projects||[]).length>0 ||
      (safe.locations||[]).length>1 ||
      (safe.users||[]).length>1
    );
    if(!hasData) return;
    _autoCompletedRef.current=true;
    const nowIso=new Date().toISOString();
    dispatch({type:"SET_CONFIG_KEY",payload:{key:"onboardingState",value:{
      ...onb,
      complete:true,
      currentStep:0,
      skipped:[],
      updatedAt:nowIso,
      completedAt:nowIso,
      autoCompletedForExistingUser:true,
    }}});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[syncLoading, currentUser?.role, safe.onboardingState?.complete, safe.onboardingState?.manuallyRestarted, safe.staff?.length, safe.vehicles?.length, safe.expenses?.length]);

  const t=T[lang];
  const isRTL=lang==="ar";

  // Wire dynamic theme
  useBrandingTheme(safe.branding);

  const switchLang=l=>{setLang(l);dispatch({type:"SET_LANG",lang:l});};
  /* Apply user's preferred language after sync is ready to avoid writing
     config before workspace hydration completes (race condition). */
  const pendingLangRef=useRef(null);
  const login=u=>{setCurrentUser(u);if(u.preferredLang){setLang(u.preferredLang);pendingLangRef.current=u.preferredLang;}try{localStorage.setItem(sessionKey,JSON.stringify(u));}catch{}};
  const logout=()=>{setCurrentUser(null);clearAuthToken();try{localStorage.removeItem(sessionKey);}catch{};try{localStorage.removeItem("cmd_push_token");}catch{};setView("Dashboard");if(onImpersonationExit)onImpersonationExit();else if(onLogoutToMain)onLogoutToMain();};

  /* ── Biometric lock screen ── */
  const [bioLocked, setBioLocked] = useState(()=> isNative() && isBiometricLockEnabled() && !!currentUser);
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioEnabled, setBioEnabled] = useState(()=> isBiometricLockEnabled());
  /* Check if biometric hardware is available on mount */
  useEffect(()=>{
    if(!isNative()) return;
    checkBiometrics().then(r=>{ setBioAvailable(r.isAvailable); });
  },[]);
  /* On cold start — if locked, prompt immediately */
  useEffect(()=>{
    if(!bioLocked) return;
    verifyBiometric("Unlock Command to continue").then(ok=>{ if(ok) setBioLocked(false); });
  },[]); // eslint-disable-line react-hooks/exhaustive-deps
  /* On app resume from background — re-lock if biometric lock is enabled */
  useEffect(()=>{
    if(!isNative()) return;
    let removeListener;
    (async()=>{
      const { App: CapApp } = await import('@capacitor/app');
      const handle = await CapApp.addListener('appStateChange', ({isActive})=>{
        if(isActive && isBiometricLockEnabled() && currentUser){
          setBioLocked(true);
          verifyBiometric("Unlock Command to continue").then(ok=>{ if(ok) setBioLocked(false); });
        }
      });
      removeListener = ()=> handle.remove();
    })();
    return ()=>{ if(removeListener) removeListener(); };
  },[currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Auto-logout when token expires (401 from authFetch) */
  useEffect(()=>{
    const onExpired=()=>logout();
    window.addEventListener("auth:expired",onExpired);
    return ()=>window.removeEventListener("auth:expired",onExpired);
  },[]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{
    if(!syncLoading&&pendingLangRef.current){
      dispatch({type:"SET_LANG",lang:pendingLangRef.current});
      pendingLangRef.current=null;
    }
  },[syncLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    document.body.className=[isRTL?"rtl":"", safe.branding?.themeMode==="dark"?"dark-theme":""].filter(Boolean).join(" ");
  },[isRTL, safe.branding?.themeMode]);

  /* Close location dropdown on outside click */
  useEffect(()=>{
    if(!locDropOpen)return;
    const h=e=>{if(locDropRef.current&&!locDropRef.current.contains(e.target)){setLocDropOpen(false);setLocSearch("");}};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[locDropOpen]);

  /* ── Periodic finance checks (run once on login, after data loaded) ── */
  useEffect(()=>{
    if(!currentUser || syncLoading) return;
    const today=new Date().toISOString().slice(0,10);
    const checkKey=`${wsKey}_finance_check_${today}`;
    try{ if(localStorage.getItem(checkKey)) return; localStorage.setItem(checkKey,"1"); }catch{}

    /* Overdue unrealised income — fire early warning based on incomeOverdueDays threshold */
    const _platIncDefs=platform?.platformIncomeAlertDefaults||{incomeOverdueDays:3};
    const _wsIncAlerts=safe.incomeAlertSettings||{};
    const _incOverdueDays=_wsIncAlerts.incomeOverdueDays??_platIncDefs.incomeOverdueDays??3;
    const _incWarningDate=new Date(new Date(today).getTime()+_incOverdueDays*86400000).toISOString().slice(0,10);
    (safe.income||[]).forEach(inc=>{
      if(inc.status==="Unrealised"&&inc.dueDate&&inc.dueDate<=_incWarningDate){
        fireNotification({eventKey:"income_overdue",state:safe,dispatch,platformCtx:platform,link:"income",eventData:{amount:inc.amount,source:inc.source,dueDate:inc.dueDate}});
      }
    });

    /* Recurring income due — notify when active recurring income is expected soon */
    const _getNextOccurrence=(startDate,frequency)=>{
      if(!startDate)return null;
      const start=new Date(startDate);const now=new Date(today);
      let d=new Date(start);
      while(d<=now){
        if(frequency==="Yearly")d.setFullYear(d.getFullYear()+1);
        else if(frequency==="Quarterly")d.setMonth(d.getMonth()+3);
        else d.setMonth(d.getMonth()+1);
      }
      return d.toISOString().slice(0,10);
    };
    (safe.recurringIncome||[]).forEach(rec=>{
      if(!rec.active)return;
      if(rec.endDate&&rec.endDate<today)return;
      const nextDue=_getNextOccurrence(rec.startDate,rec.frequency||"Monthly");
      if(nextDue&&nextDue<=_incWarningDate){
        fireNotification({eventKey:"recurring_income_due",state:safe,dispatch,platformCtx:platform,link:"income",eventData:{name:rec.name,amount:rec.amount,dueDate:nextDue}});
      }
    });

    /* Cash flow alert — current month */
    const monthStart=today.slice(0,7)+"-01";
    const monthInc=(safe.income||[]).filter(i=>i.date>=monthStart&&i.date<=today&&i.status==="Realised"&&(!i.finType||i.finType==="Income")).reduce((a,i)=>a+i.amount,0);
    const monthExp=(safe.expenses||[]).filter(e=>e.date>=monthStart&&e.date<=today&&(!e.finType||e.finType==="Expense")).reduce((a,e)=>a+e.amount,0);
    if(monthExp>monthInc&&monthExp>0){
      fireNotification({eventKey:"cashflow_alert",state:safe,dispatch,platformCtx:platform,link:"reports",eventData:{totalExpenses:monthExp,totalIncome:monthInc}});
    }

    /* Interest payment due — check active liabilities with nextPaymentDate */
    const _platInvDefs=platform?.platformInvestmentAlertDefaults||{interestPaymentDueDays:7,liabilityMaturityDays:30};
    const _wsInvAlerts=safe.investmentAlertSettings||{};
    const _interestLeadDays=_wsInvAlerts.interestPaymentDueDays??_platInvDefs.interestPaymentDueDays??7;
    const _maturityLeadDays=_wsInvAlerts.liabilityMaturityDays??_platInvDefs.liabilityMaturityDays??30;

    (safe.investmentLiabilities||[]).filter(l=>l.status==="active"&&l.nextPaymentDate).forEach(l=>{
      const dueDate=l.nextPaymentDate;
      const daysUntil=Math.ceil((new Date(dueDate)-new Date(today))/(86400000));
      if(daysUntil<=_interestLeadDays&&daysUntil>=-1){
        const effRate=l.interestType==="variable"?(parseFloat(l.baseRate)||0)+(parseFloat(l.spread)||0):parseFloat(l.interestRate)||0;
        const annual=(l.outstandingBalance||0)*effRate/100;
        const freq=l.paymentFrequency||"monthly";
        const periodAmt=freq==="annually"?annual:freq==="semi_annually"?annual/2:freq==="quarterly"?annual/4:annual/12;
        const label=daysUntil<=0?"TODAY":daysUntil===1?"tomorrow":`in ${daysUntil} days`;
        const _dueLabelAr=daysUntil<=0?"اليوم":daysUntil===1?"غداً":`خلال ${daysUntil} يوم`;
        fireNotification({eventKey:"interest_payment_due",state:safe,dispatch,platformCtx:platform,link:"investments",eventData:{liabilityName:l.name,name:l.name,currency:l.currency||"SAR",periodAmount:periodAmt,amount:periodAmt,dueLabel:label,dueLabelAr:_dueLabelAr,dueDate,lender:l.lender}});
      }
    });

    /* Liability maturity alert */
    (safe.investmentLiabilities||[]).filter(l=>l.status==="active"&&l.maturityDate).forEach(l=>{
      const daysUntil=Math.ceil((new Date(l.maturityDate)-new Date(today))/(86400000));
      if(daysUntil<=_maturityLeadDays&&daysUntil>=0){
        const label=daysUntil===0?"TODAY":daysUntil===1?"tomorrow":`in ${daysUntil} days`;
        const _matLabelAr=daysUntil===0?"اليوم":daysUntil===1?"غداً":`خلال ${daysUntil} يوم`;
        fireNotification({eventKey:"liability_maturity_alert",state:safe,dispatch,platformCtx:platform,link:"investments",eventData:{liabilityName:l.name,name:l.name,maturityDate:l.maturityDate,dueLabel:label,dueLabelAr:_matLabelAr,outstandingBalance:l.outstandingBalance,currency:l.currency||"SAR",lender:l.lender}});
      }
    });

    /* Weekly summary — fire if last was >7 days ago */
    const weeklyKey=`${wsKey}_weekly_summary`;
    try{
      const last=localStorage.getItem(weeklyKey);
      if(!last||new Date(today)-new Date(last)>=7*86400000){
        const wkStart=new Date(new Date(today).getTime()-7*86400000).toISOString().slice(0,10);
        const wkInc=(safe.income||[]).filter(i=>i.date>=wkStart&&i.date<=today&&i.status==="Realised"&&(!i.finType||i.finType==="Income")).reduce((a,i)=>a+i.amount,0);
        const wkExp=(safe.expenses||[]).filter(e=>e.date>=wkStart&&e.date<=today&&(!e.finType||e.finType==="Expense")).reduce((a,e)=>a+e.amount,0);
        if(wkInc>0||wkExp>0){
          fireNotification({eventKey:"weekly_finance_summary",state:safe,dispatch,platformCtx:platform,link:"reports",eventData:{totalIncome:wkInc,totalExpenses:wkExp}});
        }
        localStorage.setItem(weeklyKey,today);
      }
    }catch{}

    /* Supplier contract expiry alerts */
    const _supWarnDays=(safe.supplierConfig||{}).contractExpiryWarningDays||30;
    (safe.suppliers||[]).filter(s=>(s.status||"active")==="active"&&s.contractExpiry).forEach(s=>{
      const daysLeft=Math.ceil((new Date(s.contractExpiry)-new Date(today))/86400000);
      if(daysLeft>=0&&daysLeft<=_supWarnDays){
        fireNotification({eventKey:"supplier_contract_expiring",state:safe,dispatch,platformCtx:platform,link:"Suppliers",eventData:{name:s.name,contractExpiry:s.contractExpiry}});
      }
    });

    /* Monthly report — fire on 1st of month */
    if(today.endsWith("-01")){
      const prevMonth=new Date(new Date(today).getTime()-86400000);
      const pmStart=prevMonth.toISOString().slice(0,7)+"-01";
      const pmEnd=prevMonth.toISOString().slice(0,10);
      const mInc=(safe.income||[]).filter(i=>i.date>=pmStart&&i.date<=pmEnd&&i.status==="Realised"&&(!i.finType||i.finType==="Income")).reduce((a,i)=>a+i.amount,0);
      const mExp=(safe.expenses||[]).filter(e=>e.date>=pmStart&&e.date<=pmEnd&&(!e.finType||e.finType==="Expense")).reduce((a,e)=>a+e.amount,0);
      fireNotification({eventKey:"monthly_finance_report",state:safe,dispatch,platformCtx:platform,link:"reports",eventData:{totalIncome:mInc,totalExpenses:mExp}});
    }
  },[currentUser?.id, syncLoading]);

  if(syncLoading) return <><LoadingSplash appName={safe.appName} tagline={safe.tagline}/></>;
  if(!currentUser) return <><Login users={safe.users} onLogin={login} lang={lang} setLang={switchLang} appName={safe.appName} tagline={safe.tagline} branding={safe.branding} dispatch={dispatch} syncError={syncError} wsKey={wsKey}/></>;

  /* ── Biometric lock overlay — blocks UI until verified ── */
  if(bioLocked) return (<>
    
    <div style={{position:"fixed",inset:0,zIndex:99999,background:"var(--page,#FAFAF9)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:20,fontFamily:"'Inter',-apple-system,sans-serif"}}>
      <div style={{width:64,height:64,borderRadius:16,background:"var(--gold-bg,#FDFBF4)",border:"1px solid var(--gold-bd,#E8D99A)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32}}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gold,#C9A84C)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/><circle cx="12" cy="16" r="1"/></svg>
      </div>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:18,fontWeight:600,color:"var(--ink,#1A1714)",marginBottom:6}}>{safe.appName||"Command"}</div>
        <div style={{fontSize:13,color:"var(--g500,#6B6660)"}}>{lang==="ar"?"يرجى التحقق من هويتك للمتابعة":"Verify your identity to continue"}</div>
      </div>
      <button className="btn btn-p" style={{padding:"12px 32px",fontSize:14,borderRadius:10,marginTop:8}} onClick={()=>verifyBiometric(lang==="ar"?"افتح قفل Command":"Unlock Command").then(ok=>{if(ok)setBioLocked(false);})}>{lang==="ar"?"فتح القفل":"Unlock"}</button>
      <button style={{background:"none",border:"none",color:"var(--g400,#A09A94)",fontSize:12,cursor:"pointer",marginTop:4}} onClick={logout}>{lang==="ar"?"تسجيل الخروج":"Sign out"}</button>
    </div>
  </>);

  /* ── Access gate: block expired trials / cancelled subscriptions ── */
  if(subStatus&&!subStatus.access){
    const accent=safe.branding?.anchorColor||"#C9A84C";
    const reason=subStatus.reason;
    return(<>
      
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--page,#fff)",fontFamily:"'Inter',-apple-system,sans-serif"}}>
        <div style={{maxWidth:440,padding:32,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>{reason==="trial_expired"?"⏰":"⚠"}</div>
          <div style={{fontSize:22,fontWeight:700,color:"var(--ink,#1A1714)",marginBottom:8}}>
            {reason==="trial_expired"?"Your free trial has ended":"Subscription expired"}
          </div>
          <div style={{fontSize:14,color:"var(--g500,#888)",lineHeight:1.7,marginBottom:24}}>
            {reason==="trial_expired"
              ?"Your trial period has ended. Choose a plan to continue using your workspace."
              :"Your subscription has ended. Please reactivate to continue accessing your workspace."}
          </div>
          {subStatus.trialEndDate&&<div style={{fontSize:12,color:"var(--g400,#aaa)",marginBottom:16,fontFamily:"'JetBrains Mono',monospace"}}>Trial ended: {subStatus.trialEndDate}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <button onClick={()=>{
              /* Deep-link straight into the Plan & Billing tab so the user
                 lands on the reactivate action instead of having to hunt
                 for it in general settings. The subStatus override lets
                 them past this gate screen; server-side, crud.js and
                 data.js both block writes on expired/deleted workspaces,
                 so the bypass is scoped to navigation only — they can
                 reach Plan & Billing, see their invoices, and reactivate,
                 but can't silently resume adding records to an expired
                 subscription. */
              try{sessionStorage.setItem("cmd_wss_initial_tab","plan");}catch{}
              setView("WorkspaceSettings");
              setSubStatus({access:true,status:"expired_override"});
            }}
              style={{background:accent,color:"white",border:"none",borderRadius:12,padding:"14px 28px",fontSize:14,fontWeight:600,cursor:"pointer",letterSpacing:"-.01em"}}>
              {reason==="trial_expired"?"Choose a plan":"Reactivate subscription"}
            </button>
            <button onClick={()=>{logout();if(onImpersonationExit)onImpersonationExit();}}
              style={{background:"none",border:"1px solid var(--g300,#ddd)",borderRadius:8,padding:"12px 20px",fontSize:13,color:"var(--g600,#666)",cursor:"pointer"}}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    </>);
  }

  /* ── Trial banner (non-blocking) ── */
  const _trialBanner=subStatus?.status==="trial"&&subStatus.daysRemaining!=null;

  /* Staff users (drivers, maids, handymen, and any future Staff-group role)
     render into the unified StaffView. Tab visibility inside StaffView is
     driven by the staffViewCan() capability system, so new staff roles
     onboard via permission config without touching routing. */
  if(isStaffUser(currentUser,safe,platform)) return <StaffView state={safe} dispatch={dispatch} user={currentUser} onLogout={logout} t={t} switchLang={switchLang} lang={lang}/>;

  const Ico2={
    AIWorkspace:()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><circle cx="7" cy="7" r="5.5"/><path d="M4.5 8.5c0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5"/><circle cx="7" cy="5" r=".8" fill="currentColor" stroke="none"/></svg>,
    Files:()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2.5 2C2.5 1.45 2.95 1 3.5 1h4.29L11 4.21V12c0 .55-.45 1-1 1H3.5c-.55 0-1-.45-1-1V2z"/><path d="M7.5 1v3.5H11"/></svg>,
    Projects:()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x=".7" y=".7" width="5.6" height="5.6" rx="1"/><rect x=".7" y="7.7" width="5.6" height="5.6" rx="1"/><path d="M7.7 3.5h5.6M7.7 7.7h5.6M7.7 10.5h3.5"/></svg>,
    Staff:()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><circle cx="5.5" cy="4" r="2.5"/><path d="M1 12c0-2.5 2-4.5 4.5-4.5S10 9.5 10 12"/><circle cx="11" cy="5" r="2"/><path d="M11 9c1.5 0 2.5 1.2 2.5 3"/></svg>,
    Menu:()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M7 1a5 5 0 100 8A5 5 0 007 1z"/><path d="M7 9v4M5 13h4"/><path d="M4.5 5l1.5 1.5L9 4"/></svg>,
    Suppliers:()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M1 3l2-2h8l2 2v8l-2 2H3L1 11V3z"/><path d="M5 7h4M7 5v4"/></svg>,
    Transport:()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x=".7" y="4.2" width="12.6" height="5.6" rx="1.4"/><path d="M2.1 4.2l1.4-2.8h7l1.4 2.8"/><circle cx="3.5" cy="9.8" r="1.4" fill="var(--page)" stroke="currentColor"/><circle cx="10.5" cy="9.8" r="1.4" fill="var(--page)" stroke="currentColor"/></svg>,
    SmartHome:()=><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M1.4 6.3L7 1.4l5.6 4.9"/><path d="M2.8 5.9V12.6H11.2V5.9"/><circle cx="7" cy="8.4" r="1.4"/><path d="M5 10.5a2.8 2.8 0 014 0"/></svg>,
  };

  const mods = safe.modules || {};

  /* ── Package-based module permissions (enforced at nav + view level) ── */
  const _wsPkg=(platform?.platformPackages||[]).find(p=>p.id===(wsInfo?.packageId||wsInfo?.plan));
  const _pkgModSet=_wsPkg?new Set(_wsPkg.modules||[]):null;/* null = no package, all allowed */
  const pkgAllows=(k)=>k==="Dashboard"||k==="WorkspaceSettings"||k==="Settings"||!_pkgModSet||_pkgModSet.has(k);

  /* ── First-run onboarding gate (owner only) ──────────────
     Fires for owners who (a) have NEVER completed the wizard,
     AND (b) have an essentially-empty workspace. The data
     heuristic auto-backfills existing accounts that predate
     the onboarding feature — if they already have staff,
     vehicles, expenses, etc., they aren't first-run users and
     the flag is silently flipped to complete=true so the
     overlay never appears. They can still replay it from
     Settings → Restart Onboarding.
     Staff users are routed to StaffView above so they never
     hit this branch. Subscription access and sync are already
     gated above. */
  const _isOwner=currentUser?.role==="owner";
  const _onbState=safe.onboardingState||{};
  const _onbComplete=_onbState.complete===true;
  /* Data heuristic: any meaningful activity signals an
     existing workspace. Uses arrays because the sync hook
     only exposes entities as arrays. */
  const _hasExistingData=(
    (safe.staff||[]).length>0 ||
    (safe.vehicles||[]).length>0 ||
    (safe.expenses||[]).length>0 ||
    (safe.income||[]).length>0 ||
    (safe.maintenance||[]).length>0 ||
    (safe.groceries||[]).length>0 ||
    (safe.suppliers||[]).length>0 ||
    (safe.projects||[]).length>0 ||
    (safe.locations||[]).length>1 ||
    (safe.users||[]).length>1
  );
  /* Coach-mark mode: the wizard is rendered as a sibling inside
     the main return JSX (see end of this function) so the
     workspace shell is fully visible and interactive
     underneath. Shown for owners when onboarding isn't
     complete AND either (a) the workspace has no data yet
     (new user) OR (b) the user explicitly hit Restart in
     Settings. That second condition is what lets an existing
     user with data replay the tour. */
  const _manuallyRestarted=_onbState.manuallyRestarted===true;
  const _showOnboarding=_isOwner && !_onbComplete && (!_hasExistingData || _manuallyRestarted);

  // Full module catalogue — label resolution goes through moduleLabel()
  // so Arabic users always get labelAr from the registry unless the super
  // admin has set a genuinely customised name in platformModuleNames.
  const pmn=platform?.platformModuleNames||{};
  const lbl=(key)=>moduleLabel(key,{pmn,isRTL});
  const MODULE_CATALOGUE = {
    Dashboard:  {label:lbl("Dashboard"),   Icon:Ico.Grid,          badge:(safe.tasks||[]).filter(tk=>!tk.deletedAt&&tk.status!=="completed"&&tk.dueDate&&new Date(tk.dueDate)<new Date()).length||null},
    Execution:  {label:lbl("Execution"),   Icon:Ico.List,          badge:(()=>{const now=new Date();return (safe.tasks||[]).filter(tk=>!tk.deletedAt&&tk.status!=="completed"&&tk.status!=="cancelled"&&(tk.priority==="high"||tk.priority==="urgent"||(tk.slaExpectedCompletion&&new Date(tk.slaExpectedCompletion)<now))).length||null;})()},
    Projects:   {label:lbl("Projects"),    Icon:Ico2.Projects,     badge:(safe.projects||[]).filter(p=>p.status==="active").length||null},
    Income:     {label:lbl("Income"),      Icon:Ico.Income},
    Expenses:   {label:lbl("Expenses"),    Icon:Ico.Expense,       badge:safe.expenses.filter(e=>!e.approved).length||null},
    Reports:    {label:lbl("Reports"),     Icon:Ico.Finance},
    Investments:{label:lbl("Investments"), Icon:Ico.Invest},
    Household:  {label:lbl("Household"),   Icon:Ico.Home,          badge:safe.groceries.filter(g=>g.status==="pending"||g.status==="Pending").length||null},
    Facility:   {label:lbl("Facility"),    Icon:Ico.Facility,      badge:safe.maintenance.filter(m=>m.status==="pending"||m.status==="Open").length||null},
    Fleet:      {label:lbl("Fleet"),       Icon:Ico.Car,           badge:safe.vehicles.filter(v=>v.odom>=(v.nextServiceOdom||Infinity)).length||null},
    Staff:      {label:lbl("Staff"),       Icon:Ico2.Staff,        badge:(safe.leaveRequests||[]).filter(r=>r.status==="pending").length||null},
    Transport:  {label:lbl("Transport"),   Icon:Ico2.Transport,    badge:(safe.transportRequests||[]).filter(r=>r.status==="scheduled"&&r.date===TODAY).length||null},
    Menu:       {label:lbl("Menu"),        Icon:Ico2.Menu},
    Suppliers:  {label:lbl("Suppliers"),   Icon:Ico2.Suppliers},
    SmartHome:  {label:lbl("SmartHome"),   Icon:Ico2.SmartHome},
    AIWorkspace:{label:lbl("AIWorkspace"), Icon:Ico2.AIWorkspace},
    Files:      {label:lbl("Files"),       Icon:Ico2.Files},
    Calendar:   {label:lbl("Calendar"),    Icon:Ico.Cal},
  };

  // Group membership — defines which nav section header each module belongs to
  /* Group membership — imported from moduleRegistry (single source of truth) */
  const MODULE_GROUPS = REGISTRY_MODULE_GROUPS;

  const GROUP_LABELS = {
    COMMAND:t.groupCommand, FINANCE:t.finance,
    OPERATIONS:t.operations, PRODUCTIVITY:t.groupProductivity,
  };

  // Build ordered list of visible modules (respects navOrder + mods on/off)
  const navOrder = safe.navOrder || Object.keys(MODULE_CATALOGUE);
  const visibleModules = navOrder.filter(k=>(mods[k]!==false||k==="Dashboard")&&pkgAllows(k));

  // Build nav with smart group headers (only show group if it has ≥1 visible child)
  const nav = [];
  let lastGroup = null;
  visibleModules.forEach(key => {
    const group = MODULE_GROUPS[key];
    if(group && group !== lastGroup) {
      nav.push({grp: GROUP_LABELS[group]||group, grpKey: group});
      lastGroup = group;
    }
    const mod = MODULE_CATALOGUE[key];
    if(mod) nav.push({...mod, v: key});
  });
  // Always add Settings at end
  nav.push({grp:t.groupSystem,grpKey:"SYSTEM"});
  nav.push({label:t.workspaceSettings, v:"WorkspaceSettings", Icon:Ico.Gear});
  nav.push({label:t.moduleSettings, v:"Settings", Icon:Ico.Gear});

  const titles={
    Dashboard:  lbl("Dashboard"),   Calendar:    lbl("Calendar"),
    Execution:  lbl("Execution"),   Projects:    lbl("Projects"),
    Income:     lbl("Income"),      Expenses:    lbl("Expenses"),
    Reports:    lbl("Reports"),     Investments: lbl("Investments"),
    Household:  lbl("Household"),   Facility:    lbl("Facility"),
    Fleet:      lbl("Fleet"),       Staff:       lbl("Staff"),
    Transport:  lbl("Transport"),   Menu:        lbl("Menu"),
    Suppliers:  lbl("Suppliers"),   Settings:    t.moduleSettings,
    WorkspaceSettings: t.workspaceSettings,
    AIWorkspace:lbl("AIWorkspace"), Files:       lbl("Files"),
  };
  const locLabel=safe.locations.find(l=>l.id===safe.locFilter)?.name;

  return(
    <>
      
      <div className={`app ${isRTL?"rtl":""}`}>
        {/* Trial countdown banner */}
        {_trialBanner&&(
          <div style={{background:"linear-gradient(90deg,#FEF3C7,#FDE68A)",padding:"8px 16px",textAlign:"center",fontSize:12,fontWeight:600,color:"#92400E",position:"relative",zIndex:100}}>
            Free trial: {subStatus.daysRemaining} day{subStatus.daysRemaining!==1?"s":""} remaining
            {subStatus.daysRemaining<=3&&<span style={{marginInlineStart:8,opacity:.8}}>— upgrade now to avoid interruption</span>}
          </div>
        )}
        {/* Sync error banner — surfaces hydrate/refetch failures so the user sees what's wrong */}
        {syncError&&(
          <div style={{background:"#FEE2E2",borderBottom:"1px solid #FCA5A5",padding:"10px 16px",fontSize:12,fontWeight:600,color:"#991B1B",position:"relative",zIndex:100,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
            <span>⚠ Sync error: {syncError}</span>
            <button
              onClick={()=>{clearAuthToken();try{localStorage.clear();}catch{};window.location.reload();}}
              style={{fontSize:11,fontWeight:600,background:"#991B1B",color:"#fff",border:"none",padding:"5px 12px",borderRadius:6,cursor:"pointer"}}>
              Clear cache &amp; reload
            </button>
          </div>
        )}
        {sidebarOpen&&<div className="sb-overlay open" onClick={()=>setSidebarOpen(false)}/>}
        <div className={`sb ${sidebarOpen?"open":""}`}>
          <div className="sb-top">
            {(()=>{
              const pLogo=platform?.platformLogo||platform?.platformLogoLight;
              const pName=platform?.platformName||safe.appName||"TR OS";
              return pLogo
                ? <img src={pLogo} alt={pName} style={{maxHeight:32,maxWidth:180,objectFit:"contain",marginBottom:14}}/>
                : <div className="sb-wordmark">{pName.split(" ").map((w,i)=>i===0?<span key={i} style={{WebkitTextFillColor:"var(--ink)"}}>{w} </span>:<span key={i}>{w}</span>)}</div>;
            })()}
{/* tagline removed from sidebar */}
            <div className="sb-loc-lbl">{t.location}</div>
            <div className="sb-loc-wrap" ref={locDropRef}>
              <button className={`sb-loc-trigger${locDropOpen?" open":""}`} onClick={()=>{setLocDropOpen(o=>!o);setLocSearch("");}}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--g400)" strokeWidth="1.4" style={{flexShrink:0}}><path d="M7 1.5C4.5 1.5 2.5 3.8 2.5 6c0 3.5 4.5 6.5 4.5 6.5s4.5-3 4.5-6.5c0-2.2-2-4.5-4.5-4.5z"/><circle cx="7" cy="6" r="1.5"/></svg>
                <span className="sb-loc-trigger-text">{safe.locFilter?((l)=>isRTL&&l?.nameAr?l.nameAr:l?.name||t.allLocations)(safe.locations.find(l=>l.id===safe.locFilter)):t.allLocations}</span>
                <span className="sb-loc-trigger-chevron">▾</span>
              </button>
              {locDropOpen&&(
                <div className="sb-loc-dropdown">
                  <div style={{position:"relative"}}>
                    <svg className="sb-loc-search-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="5" cy="5" r="3.5"/><path d="M8 8l2.5 2.5"/></svg>
                    <input className="sb-loc-search" value={locSearch} onChange={e=>setLocSearch(e.target.value)} placeholder={isRTL?"بحث...":"Search..."} autoFocus/>
                  </div>
                  <div className="sb-loc-list">
                    {(()=>{
                      const q=locSearch.toLowerCase();
                      const filtered=[{id:"",name:t.allLocations,nameAr:t.allLocations},...safe.locations].filter(l=>{
                        if(!q)return true;
                        return (l.name||"").toLowerCase().includes(q)||(l.nameAr||"").toLowerCase().includes(q);
                      });
                      return filtered.length?filtered.map(l=>(
                        <div key={l.id||"_all"} className={`sb-loc-item${safe.locFilter===l.id?" active":""}`} onClick={()=>{dispatch({type:"SET_LOC",id:l.id});setLocDropOpen(false);setLocSearch("");}}>
                          {isRTL&&l.nameAr?l.nameAr:l.name}
                        </div>
                      )):<div className="sb-loc-empty">{isRTL?"لا توجد نتائج":"No results"}</div>;
                    })()}
                  </div>
                </div>
              )}
            </div>
            <div className="sb-sep"/>
          </div>
          <div className="sb-nav">
            {nav.map((it,i)=>{
              if(it.grp!==undefined) return it.grp?<div key={i} className="nav-grp">{it.grp}</div>:<div key={i} style={{height:4}}/>;
              return(
                <div key={it.v} className={`nav-it ${view===it.v?"active":""}`} onClick={()=>{
                  if(view===it.v){/* Re-tap: scroll to top (iOS convention) */try{document.querySelector(".main > div").scrollTo({top:0,behavior:"smooth"});}catch{}}
                  else setView(it.v);
                  setSidebarOpen(false);
                }}>
                  <div className="nav-ico"><it.Icon/></div>
                  {it.label}
                  {it.badge>0&&<span className="nav-badge">{it.badge}</span>}
                </div>
              );
            })}
          </div>
          <div className="sb-foot">
            {isNative()&&bioAvailable&&(
              <div style={{padding:"10px 14px",borderTop:"1px solid var(--g100)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--g500)" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/><circle cx="12" cy="16" r="1"/></svg>
                  <span style={{fontSize:12,color:"var(--g600)"}}>{lang==="ar"?"قفل بيومتري":"Biometric Lock"}</span>
                </div>
                <label style={{position:"relative",display:"inline-block",width:36,height:20,flexShrink:0}}>
                  <input type="checkbox" checked={bioEnabled} onChange={async(e)=>{
                    if(e.target.checked){
                      const ok=await verifyBiometric(lang==="ar"?"تفعيل القفل البيومتري":"Enable biometric lock");
                      if(ok){setBiometricLockEnabled(true);setBioEnabled(true);hapticSuccess();}
                    }else{setBiometricLockEnabled(false);setBioEnabled(false);hapticTap();}
                  }} style={{opacity:0,width:0,height:0}}/>
                  <span style={{position:"absolute",cursor:"pointer",inset:0,background:bioEnabled?"var(--gold)":"var(--g200)",borderRadius:20,transition:"background .2s"}}/>
                  <span style={{position:"absolute",left:bioEnabled?18:2,top:2,width:16,height:16,background:"white",borderRadius:"50%",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.15)"}}/>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="main">
          <div className="topbar">
            <button className="hamburger" onClick={()=>setSidebarOpen(o=>!o)} aria-label="Menu">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <line x1="3" y1="5" x2="15" y2="5"/><line x1="3" y1="9" x2="12" y2="9"/><line x1="3" y1="13" x2="15" y2="13"/>
              </svg>
            </button>
            <div className="tb-title">{safe.appName||"TR OS"}</div>
            {locLabel&&<div className="tb-loc">{locLabel}</div>}
            <div className="tb-date">{LABEL.toUpperCase()}</div>
            <button className="tb-help" onClick={()=>window.dispatchEvent(new CustomEvent("help:open"))} aria-label={lang==="ar"?"مساعدة":"Help"} title={lang==="ar"?"مساعدة":"Help"}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.1 9a3 3 0 015.8 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </button>
            <NotifBell notifications={safe.notifications||[]} userId={currentUser.id} dispatch={dispatch} lang={lang}/>
            <div className="sb-lang" style={{padding:0,gap:4,display:"flex"}}>
              <button className={`lang-btn ${lang==="en"?"active":""}`} onClick={()=>switchLang("en")} style={{padding:"4px 10px",fontSize:10}}>EN</button>
              <button className={`lang-btn ${lang==="ar"?"active":""}`} onClick={()=>switchLang("ar")} style={{padding:"4px 10px",fontSize:10}}>ع</button>
            </div>
            <div className="sb-user" onClick={logout} title="Sign out" style={{padding:"4px 8px",gap:8,margin:0}}>
              <div className="sb-ava" style={{width:28,height:28,fontSize:10}}>{currentUser.initials}</div>
              <div className="sb-uname" style={{fontSize:12}}>{isRTL&&currentUser.nameAr?currentUser.nameAr:currentUser.name}</div>
            </div>
          </div>
          <div style={{flex:1,overflowY:"auto",background:"var(--page)",position:"relative"}}>
            <OfflineBanner/>
            {/* Guard: redirect to Dashboard if view targets a module not in package */}
            {!pkgAllows(view)&&view!=="Dashboard"&&(()=>{setView("Dashboard");return null;})()}
            <div key={view} className="view-content">
            {/* Per-module ErrorBoundary: a crash inside one module now
                shows a localized recovery screen instead of unmounting
                the whole workspace shell (sidebar, nav, user menu).
                The outer <div key={view}> already re-mounts on view
                change, so the boundary naturally resets when the user
                navigates to a different module. */}
            <ErrorBoundary
              name={`module:${view}`}
              onReset={() => setView("Dashboard")}
            >
            {view==="Dashboard"     && <CommandCenter state={safe} dispatch={dispatch} t={t} setView={setView}/>}
            {view==="Calendar"      && <CalendarModule state={safe} dispatch={dispatch} t={t}/>}
            {view==="Execution"     && <Execution state={safe} dispatch={dispatch} t={t} TaskEnginePanel={TaskEnginePanel}/>}
            {view==="Projects"      && <ProjectsModule state={safe} dispatch={dispatch} t={t} currentUser={currentUser}/>}
            {view==="Income"        && <IncomeModule state={safe} dispatch={dispatch} t={t}/>}
            {view==="Expenses"      && <ExpensesModule state={safe} dispatch={dispatch} t={t} defaultUser={currentUser?.id} currentUser={currentUser} platformCtx={platformCtx}/>}
            {view==="Reports"       && <ReportsModule       state={safe} dispatch={dispatch} t={t}/>}
            {view==="Investments"   && <Investments state={safe} dispatch={dispatch} t={t} currentUser={currentUser} platformCtx={platformCtx}/>}
            {view==="Household"     && <HouseholdOps state={safe} dispatch={dispatch} t={t}/>}
            {view==="Facility"      && <FacilityMgmt state={safe} dispatch={dispatch} t={t} platformCtx={platformCtx}/>}
            {view==="Fleet"         && <Fleet state={safe} dispatch={dispatch} t={t} platformCtx={platformCtx}/>}
            {view==="AIWorkspace"   && <AIWorkspace lang={lang} wsApiKeys={safe.apiKeys}/>}
            {view==="Files"         && <FilesModule lang={lang}/>}
            {view==="Staff"         && <StaffModule     state={safe} dispatch={dispatch} forceUpdate={forceUpdate} t={t} currentUser={currentUser}/>}
            {view==="Transport"     && <TransportModule  state={safe} dispatch={dispatch} t={t} platformCtx={platformCtx} currentUser={currentUser}/>}
            {view==="Menu"          && <MenuModule       state={safe} dispatch={dispatch} t={t} wsKey={wsKey} currentUser={currentUser}/>}
            {view==="Suppliers"     && <SuppliersModule  state={safe} dispatch={dispatch} t={t} platformCtx={platformCtx}/>}
            {view==="SmartHome"    && <SmartHomeModule   state={safe} dispatch={dispatch} t={t} currentUser={currentUser} platformCtx={platformCtx}/>}
            {view==="WorkspaceSettings" && currentUser?.role==="owner" && <WorkspaceSettings state={safe} dispatch={dispatch} t={t} currentUser={currentUser} pdispatch={pdispatch} wsInfo={wsInfo} PlacesAutocomplete={PlacesAutocomplete} MoyasarPaymentInit={MoyasarPaymentInit} buildModulesFromPackage={buildModulesFromPackage}/>}
            {view==="Settings"      && currentUser?.role==="owner" && <Settings state={safe} dispatch={dispatch} t={t} currentUser={currentUser} pdispatch={pdispatch} wsInfo={wsInfo}/>}
            </ErrorBoundary>
            </div>
          </div>
        </div>
      </div>
      <HelpBubble
        currentView={view}
        locale={lang}
        enabledModules={REGISTRY_MODULE_KEYS.filter(k=>pkgAllows(k)&&mods[k]!==false)}
      />
      <BottomNav view={view} setView={setView} mods={mods} pkgAllows={pkgAllows} MODULE_CATALOGUE={MODULE_CATALOGUE} t={t} currentUser={currentUser} isRTL={isRTL} lang={lang} onSwitchLang={switchLang} mobileLabels={platform?.platformMobileLabels||{}} navOrder={safe.navOrder} onLogout={logout} onFabPress={(action)=>{
        /* Dispatch custom event that the active module component listens for */
        window.dispatchEvent(new CustomEvent("bnav:fab",{detail:{action,view}}));
      }}/>
      {/* ── First-run onboarding coach-mark ──
          Rendered as a sibling (not a gate) so the app shell
          sits beneath it and remains interactive. The wizard
          manages its own overlay/backdrop. Hidden entirely for
          existing users and for workspaces where the flag is
          already complete. */}
      {_showOnboarding && (
        <OnboardingWizard
          state={safe}
          dispatch={dispatch}
          currentUser={currentUser}
          platform={platform}
          pkgAllows={pkgAllows}
          platformModuleNames={platform?.platformModuleNames||{}}
          lang={lang}
          switchLang={switchLang}
          navigateTo={(v)=>setView(v)}
          currentView={view}
        />
      )}
    </>
  );
}
